import React, { useMemo, useState } from "react";
import InfoTip from "../common/InfoTip";
import { useQuery, gql } from "@apollo/client";
import { downloadCsv, toCsv } from "./leaveShared";

// Leave report for a date range: totals, days off by type per month, and
// per professional how much was off, how much of it unplanned and what it
// cost. Every number is defined in barbera_backend's
// leave-report.service.js; the CSVs carry the same numbers.

const GET_LEAVE_REPORT = gql`
  query GetLeaveReport($startDate: String!, $endDate: String!) {
    getLeaveReport(startDate: $startDate, endDate: $endDate) {
      status
      message
      totals {
        professionals
        workingDays
        leaveDays
        absentDays
        unplannedDays
        absenteeismRate
        penaltyImposed
        penaltyWaived
        retrainingFees
        pendingRequests
      }
      byTypeMonth {
        month
        leaveType
        approvedDays
        absentDays
      }
      perEmployee {
        employeeId
        fullName
        leftOn
        workingDays
        leaveDays
        absentDays
        unplannedDays
        unplannedSpells
        requests
        absenteeismRate
        bradfordFactor
        penaltyImposed
        penaltyWaived
        retrainingFees
      }
    }
  }
`;

// One colour per leave type, absences always dark
const TYPE_COLORS = ["bg-blue-500", "bg-teal-500", "bg-violet-500", "bg-amber-500", "bg-pink-500", "bg-lime-600"];
const ABSENT_COLOR = "bg-gray-800";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const num = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
const pct = (r) => `${(r * 100).toFixed(1)}%`;
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const monthLabel = (m) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });

const PRESETS = [
  ["This month", (t) => [new Date(t.getFullYear(), t.getMonth(), 1), new Date(t.getFullYear(), t.getMonth() + 1, 0)]],
  ["Last month", (t) => [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]],
  ["Last 3 months", (t) => [new Date(t.getFullYear(), t.getMonth() - 2, 1), new Date(t.getFullYear(), t.getMonth() + 1, 0)]],
  ["This year", (t) => [new Date(t.getFullYear(), 0, 1), new Date(t.getFullYear(), 11, 31)]],
];

const COLUMNS = [
  [
    "fullName",
    "Professional",
    (r) => (
      <>
        {r.fullName}
        {r.leftOn && (
          <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-[11px] font-normal text-gray-500">
            {r.leftOn === "unknown" ? "left" : `left ${r.leftOn}`}
          </span>
        )}
      </>
    ),
    "Everyone who was in the company at any point in this period. \"left …\" is their last working day.",
  ],
  [
    "workingDays",
    "Working days",
    (r) => r.workingDays,
    "Days they were employed in this period, from joining to last working day. Every day counts - there is no weekly off.",
  ],
  [
    "leaveDays",
    "Leave days",
    (r) => num(r.leaveDays),
    "Approved leave on those days (half day = 0.5). Paid and unpaid leave both count.",
  ],
  [
    "absentDays",
    "Absent days",
    (r) => num(r.absentDays),
    "Days marked Absent: did not work and had no approved leave - including automatic absences for not tapping Mark present.",
  ],
  [
    "unplannedDays",
    "Unplanned days",
    (r) => num(r.unplannedDays),
    "Absent days plus approved leave applied with less notice than the leave type's notice days. These are the days the office could not plan for.",
  ],
  [
    "absenteeismRate",
    "Absenteeism",
    (r) => pct(r.absenteeismRate),
    "(Leave days + absent days) ÷ working days. 10% = one day off in every ten.",
  ],
  [
    "bradfordFactor",
    "Bradford",
    (r) => num(r.bradfordFactor),
    "Unplanned spells² × unplanned days. It flags frequent short unplanned absences: five 1-day absences = 5² × 5 = 125, one 5-day absence = 1² × 5 = 5. Above 50 is shown in red - worth a conversation.",
  ],
  [
    "penaltyImposed",
    "Fines",
    (r) => inr(r.penaltyImposed),
    "Leave deductions for days in this period: the leave fine plus, on unpaid days, a day's salary. Waived ones are not included.",
  ],
  [
    "penaltyWaived",
    "Waived",
    (r) => inr(r.penaltyWaived),
    "Fines switched to Warning (waived from Payouts or a dispute) - shown but not deducted.",
  ],
  [
    "retrainingFees",
    "Retraining",
    (r) => inr(r.retrainingFees),
    "Retraining fees dated in this period - charged once when a single leave reaches the retraining threshold (days).",
  ],
];

const Tile = ({ label, value, hint, info }) => (
  <div className="p-4 rounded-lg border bg-white">
    <div className="text-xs font-semibold uppercase text-gray-500">
      {label}
      {info && <InfoTip title={label} text={info} />}
    </div>
    <div className="text-2xl font-bold text-gray-800 mt-1">{value}</div>
    {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

const LeaveReports = () => {
  const [range, setRange] = useState(() => {
    const [s, e] = PRESETS[0][1](new Date());
    return { startDate: ymd(s), endDate: ymd(e) };
  });
  const [sort, setSort] = useState({ key: "absenteeismRate", desc: true });

  const { data, loading } = useQuery(GET_LEAVE_REPORT, {
    variables: range,
    fetchPolicy: "network-only",
    skip: !range.startDate || !range.endDate,
  });
  const report = data?.getLeaveReport;
  const totals = report?.totals;
  const byTypeMonth = useMemo(() => report?.byTypeMonth || [], [report]);

  const types = useMemo(() => [...new Set(byTypeMonth.map((r) => r.leaveType))].sort(), [byTypeMonth]);
  const months = useMemo(() => {
    const map = {};
    byTypeMonth.forEach((r) => {
      if (!map[r.month]) map[r.month] = { month: r.month, byType: {}, absent: 0, total: 0 };
      map[r.month].byType[r.leaveType] = (map[r.month].byType[r.leaveType] || 0) + r.approvedDays;
      map[r.month].absent += r.absentDays;
      map[r.month].total += r.approvedDays + r.absentDays;
    });
    return Object.values(map).sort((a, b) => a.month.localeCompare(b.month));
  }, [byTypeMonth]);
  const maxMonth = Math.max(1, ...months.map((m) => m.total));

  const people = useMemo(() => {
    const rows = [...(report?.perEmployee || [])];
    rows.sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      const cmp = typeof x === "string" ? String(x).localeCompare(String(y)) : x - y;
      return sort.desc ? -cmp : cmp;
    });
    return rows;
  }, [report, sort]);

  const exportPeople = () =>
    downloadCsv(
      `leave-report-professionals_${range.startDate}_${range.endDate}.csv`,
      toCsv(people, [
        ["Professional", (r) => r.fullName],
        ["Left on", (r) => r.leftOn || ""],
        ["Working days", (r) => r.workingDays],
        ["Leave days", (r) => r.leaveDays],
        ["Absent days", (r) => r.absentDays],
        ["Unplanned days", (r) => r.unplannedDays],
        ["Unplanned spells", (r) => r.unplannedSpells],
        ["Requests", (r) => r.requests],
        ["Absenteeism rate (%)", (r) => (r.absenteeismRate * 100).toFixed(1)],
        ["Bradford factor", (r) => r.bradfordFactor],
        ["Fines imposed (₹)", (r) => r.penaltyImposed],
        ["Fines waived (₹)", (r) => r.penaltyWaived],
        ["Retraining fees (₹)", (r) => r.retrainingFees],
      ]),
    );

  const exportTypes = () =>
    downloadCsv(
      `leave-report-by-type_${range.startDate}_${range.endDate}.csv`,
      toCsv(byTypeMonth, [
        ["Month", (r) => r.month],
        ["Leave type", (r) => r.leaveType],
        ["Approved days", (r) => r.approvedDays],
        ["Absent days", (r) => r.absentDays],
      ]),
    );

  const header = (key, label, info) => (
    <th
      key={key}
      onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== "fullName" }))}
      className="py-2 px-3 cursor-pointer select-none whitespace-nowrap"
    >
      {label}
      {sort.key === key ? (sort.desc ? " ↓" : " ↑") : ""}
      {info && <InfoTip title={label} text={info} align={key === "retrainingFees" || key === "penaltyWaived" ? "right" : "left"} />}
    </th>
  );

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-gray-50 p-4 rounded-lg border">
        {["startDate", "endDate"].map((key) => (
          <label key={key} className="block">
            <span className="block text-sm font-medium mb-1">{key === "startDate" ? "From" : "To"}</span>
            <input
              type="date"
              className="border p-2 rounded bg-white"
              value={range[key]}
              onChange={(e) => setRange((r) => ({ ...r, [key]: e.target.value }))}
            />
          </label>
        ))}
        {PRESETS.map(([label, fn]) => (
          <button
            key={label}
            onClick={() => {
              const [s, e] = fn(new Date());
              setRange({ startDate: ymd(s), endDate: ymd(e) });
            }}
            className="text-sm px-3 py-2 border rounded bg-white hover:bg-gray-100"
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-gray-500">Building report...</p>}
      {report && report.status !== "SUCCESS" && (
        <p className="text-sm text-red-600">{report.message}</p>
      )}

      {totals && (
        <>
          <div className="grid grid-cols-5 gap-3 mb-6">
            <Tile
              label="Absenteeism"
              value={pct(totals.absenteeismRate)}
              hint={`of ${totals.workingDays} working days, ${totals.professionals} professionals`}
              info="(Leave days + absent days) ÷ working days, for everyone in the company during the period."
            />
            <Tile
              label="Leave days"
              value={num(totals.leaveDays)}
              hint="approved"
              info="Approved leave days in the period (half day = 0.5), paid and unpaid."
            />
            <Tile
              label="Absent days"
              value={num(totals.absentDays)}
              hint={`${num(totals.unplannedDays)} unplanned incl. short notice`}
              info="Days marked Absent (no approved leave). Unplanned = absent days + leave applied with less than the type's notice days."
            />
            <Tile
              label="Fines"
              value={inr(totals.penaltyImposed)}
              hint={`${inr(totals.penaltyWaived)} waived · ${inr(totals.retrainingFees)} retraining`}
              info="Leave deductions in the period (fine + a day's salary on unpaid days). Waived fines and retraining fees are shown separately."
            />
            <Tile
              label="Pending now"
              value={totals.pendingRequests}
              hint="requests awaiting a decision"
              info="Leave requests waiting for approval right now, whatever their dates."
            />
          </div>

          <div className="border rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-800">Days off by month and type</h3>
              <button
                onClick={exportTypes}
                disabled={byTypeMonth.length === 0}
                className="text-sm px-3 py-1.5 border rounded bg-white hover:bg-gray-100 disabled:opacity-40"
              >
                Export CSV
              </button>
            </div>
            {months.length === 0 ? (
              <p className="text-sm text-gray-500">No approved leave or absences in this range.</p>
            ) : (
              <>
                <div className="space-y-2">
                  {months.map((m) => (
                    <div key={m.month} className="flex items-center gap-3 text-sm">
                      <span className="w-20 shrink-0 text-gray-600">{monthLabel(m.month)}</span>
                      <div className="flex-1 flex h-5 rounded overflow-hidden bg-gray-100">
                        {types.map((t, i) =>
                          m.byType[t] ? (
                            <div
                              key={t}
                              className={TYPE_COLORS[i % TYPE_COLORS.length]}
                              style={{ width: `${(m.byType[t] / maxMonth) * 100}%` }}
                              title={`${t}: ${num(m.byType[t])} days`}
                            />
                          ) : null,
                        )}
                        {m.absent > 0 && (
                          <div
                            className={ABSENT_COLOR}
                            style={{ width: `${(m.absent / maxMonth) * 100}%` }}
                            title={`Absent: ${num(m.absent)} days`}
                          />
                        )}
                      </div>
                      <span className="w-16 shrink-0 text-right font-medium text-gray-800">
                        {num(m.total)} d
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
                  {types.map((t, i) => (
                    <span key={t} className="flex items-center gap-1">
                      <span className={`w-3 h-3 rounded ${TYPE_COLORS[i % TYPE_COLORS.length]}`} />
                      {t}
                    </span>
                  ))}
                  <span className="flex items-center gap-1">
                    <span className={`w-3 h-3 rounded ${ABSENT_COLOR}`} />
                    Absent (any type)
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="border rounded-lg">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="font-semibold text-gray-800">By professional</h3>
                <p className="text-xs text-gray-500">
                  Working days = every day employed in the range. Unplanned = absent, or applied with less than the
                  type's notice. Bradford = unplanned spells² × unplanned days; above ~50 is worth a
                  conversation.
                </p>
              </div>
              <button
                onClick={exportPeople}
                disabled={people.length === 0}
                className="shrink-0 ml-4 text-sm px-3 py-1.5 border rounded bg-white hover:bg-gray-100 disabled:opacity-40"
              >
                Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>{COLUMNS.map(([key, label, , info]) => header(key, label, info))}</tr>
                </thead>
                <tbody>
                  {people.map((r) => (
                    <tr key={r.employeeId} className="border-b hover:bg-gray-50">
                      {COLUMNS.map(([key, , show]) => (
                        <td
                          key={key}
                          className={`py-2 px-3 ${key === "fullName" ? "font-medium text-gray-800" : ""} ${
                            key === "bradfordFactor" && r.bradfordFactor >= 50 ? "text-red-600 font-semibold" : ""
                          }`}
                        >
                          {show(r)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LeaveReports;
